import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton,
  SvgIcon
} from '@mui/material';
import MuiGrid from '../MuiGrid';
import PlayerCircle, { PlayerState, PlayerRole } from './PlayerCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PanToolIcon from '@mui/icons-material/PanTool';

interface ArrowProps {
  players: PlayerState[];
  selectedId: number;
  targetId: number;
  calculatePosition: (index: number, total: number, radius: number) => { left: number; top: number };
  totalPlayers: number;
  radius: number;
}

// Component to draw an arrow between two player circles
const ArrowBetweenPlayers: React.FC<ArrowProps> = ({ 
  players, 
  selectedId, 
  targetId,
  calculatePosition,
  totalPlayers,
  radius
}) => {
  // Find the positions of the selected player and target
  const selectedIndex = players.findIndex(p => p.id === selectedId);
  const targetIndex = players.findIndex(p => p.id === targetId);
  
  if (selectedIndex === -1 || targetIndex === -1) return null;
  
  const start = calculatePosition(selectedIndex, totalPlayers, radius);
  const end = calculatePosition(targetIndex, totalPlayers, radius);
  
  // Calculate the SVG path
  // Adjust the path to start and end at the edges of the circles
  const angle = Math.atan2(end.top - start.top, end.left - start.left);
  const circleRadius = 32.5; // Half of the player circle size (65/2)
  
  // Adjust start and end points to be on the edge of the circles
  const adjustedStart = {
    x: start.left + Math.cos(angle) * circleRadius,
    y: start.top + Math.sin(angle) * circleRadius
  };
  
  const adjustedEnd = {
    x: end.left - Math.cos(angle) * circleRadius,
    y: end.top - Math.sin(angle) * circleRadius
  };
  
  // Create an arrow path
  const dx = adjustedEnd.x - adjustedStart.x;
  const dy = adjustedEnd.y - adjustedStart.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  
  // Make sure we have enough length for an arrow
  if (length < 10) return null;
  
  // Arrow head size
  const arrowHeadSize = 12;
  
  // Calculate path with arrow head
  const path = `
    M ${adjustedStart.x} ${adjustedStart.y}
    L ${adjustedEnd.x} ${adjustedEnd.y}
  `;
  
  // Create arrow head as a separate triangle
  const arrowHead = `
    M ${adjustedEnd.x} ${adjustedEnd.y}
    L ${adjustedEnd.x - arrowHeadSize * Math.cos(angle - Math.PI/6)} ${adjustedEnd.y - arrowHeadSize * Math.sin(angle - Math.PI/6)}
    L ${adjustedEnd.x - arrowHeadSize * Math.cos(angle + Math.PI/6)} ${adjustedEnd.y - arrowHeadSize * Math.sin(angle + Math.PI/6)}
    Z
  `;
  
  return (
    <Box 
      component="svg" 
      sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}
    >
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="7"
          refX="0"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="#ff9800" />
        </marker>
      </defs>
      <path
        d={path}
        fill="none"
        stroke="#ff9800"
        strokeWidth="3"
        strokeDasharray="5,5"
        opacity="0.8"
        markerEnd="url(#arrowhead)"
      />
      <path
        d={arrowHead}
        fill="#ff9800"
        opacity="0.8"
      />
    </Box>
  );
};

interface MafiaSimulatorProps {
  initialStates?: PlayerState[];
  onSave?: (states: PlayerState[]) => void;
  readOnly?: boolean;
}

const MafiaSimulator: React.FC<MafiaSimulatorProps> = ({ initialStates, onSave, readOnly = false }) => {
  const [players, setPlayers] = useState<PlayerState[]>([]);
  const [currentAction, setCurrentAction] = useState<'role' | 'alive' | 'check' | 'vote'>('role');
  const [selectedVoter, setSelectedVoter] = useState<number | null>(null);
  const [hoverTarget, setHoverTarget] = useState<number | null>(null);

  // Initialize with default players or provided initial states
  useEffect(() => {
    if (initialStates && initialStates.length > 0) {
      setPlayers(initialStates);
    } else {
      const defaultPlayers = Array.from({ length: 10 }, (_, i) => ({
        id: i + 1,
        role: 'civilian' as PlayerRole,
        isAlive: true,
        isCheckedBySheriff: false,
        votesFor: []
      }));
      setPlayers(defaultPlayers);
    }
  }, [initialStates]);

  const resetToDefault = () => {
    const defaultPlayers = Array.from({ length: 10 }, (_, i) => ({
      id: i + 1,
      role: 'civilian' as PlayerRole,
      isAlive: true,
      isCheckedBySheriff: false,
      votesFor: []
    }));
    setPlayers(defaultPlayers);
    setSelectedVoter(null);
    setHoverTarget(null);
  };

  const handlePlayerClick = (id: number) => {
    if (readOnly) return; // Disable interactions in read-only mode

    if (currentAction === 'vote') {
      // Handle voting logic
      if (selectedVoter === null) {
        // First click in vote mode selects the voter
        setSelectedVoter(id);
        setHoverTarget(null);
      } else if (selectedVoter === id) {
        // Clicking the same player cancels the selection
        setSelectedVoter(null);
        setHoverTarget(null);
      } else {
        // Second click adds a vote from selectedVoter to clicked player
        setHoverTarget(null);
        setPlayers(prev => prev.map(player => {
          if (player.id === selectedVoter) {
            // Ensure votesFor is initialized
            const currentVotes = player.votesFor || [];
            
            // Add the voted player to the voter's votesFor array if not already there
            const updatedVotes = currentVotes.includes(id) 
              ? currentVotes.filter(vote => vote !== id) // Remove if already voted
              : [...currentVotes, id]; // Add if not voted yet
            
            return { ...player, votesFor: updatedVotes };
          }
          return player;
        }));
        // Reset states after vote is recorded
        setSelectedVoter(null);
        setHoverTarget(null);
      }
    } else {
      // Handle other actions
      setPlayers(prev => prev.map(player => {
        if (player.id === id) {
          switch (currentAction) {
            case 'role':
              // Cycle through roles: civilian -> mafia -> don -> sheriff -> unknown -> civilian
              const nextRoles: Record<PlayerRole, PlayerRole> = {
                'civilian': 'mafia',
                'mafia': 'don',
                'don': 'sheriff',
                'sheriff': 'unknown',
                'unknown': 'civilian'
              };
              return { ...player, role: nextRoles[player.role] };
            
            case 'alive':
              return { ...player, isAlive: !player.isAlive };
            
            case 'check':
              return { ...player, isCheckedBySheriff: !player.isCheckedBySheriff };
              
            default:
              return player;
          }
        }
        return player;
      }));
    }
  };

  const handlePlayerRightClick = (id: number) => {
    if (readOnly) return; // Disable interactions in read-only mode

    if (currentAction === 'vote') {
      // Right-click in vote mode clears all votes for this player
      setPlayers(prev => prev.map(player => 
        player.id === id ? { ...player, votesFor: [] } : player
      ));
      // Reset selected voter if this was the one selected
      if (selectedVoter === id) {
        setSelectedVoter(null);
        setHoverTarget(null);
      }
    } else {
      // Right-click to toggle alive/dead status in other modes
      setPlayers(prev => prev.map(player => 
        player.id === id ? { ...player, isAlive: !player.isAlive } : player
      ));
    }
  };

  // Calculate positions in a horseshoe arrangement with a gap at the top
  const calculatePosition = (index: number, total: number, radius: number) => {
    // Use a horseshoe layout:
    // - Players are arranged in a 300-degree arc (leaving 60-degree gap at top center)
    // - Starting from 10:30 position (player 1) to 1:30 position (player 10)
    
    // Starting angle at 10:30 position (approx. -π/6 from vertical/top)
    const startAngle = -5 * Math.PI / 12;
    
    // End angle at 1:30 position (approx. π/6 from vertical/top)
    const endAngle = 5 * Math.PI / 12 + Math.PI;
    
    // Calculate the total angle of the arc
    const arcAngle = endAngle - startAngle;
    
    // Calculate angle step between players
    const angleStep = arcAngle / (total - 1);
    
    // Calculate angle for current player
    const angle = startAngle + index * angleStep;
    
    // Calculate position using standard circle calculations
    return {
      left: radius + radius * Math.cos(angle),
      top: radius + radius * Math.sin(angle)
    };
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2, maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 0 }}>
          Mafia Game Simulator
          <Tooltip title="Click to change a player's role, status, or check. Right-click always toggles alive/dead status.">
            <IconButton size="small" sx={{ ml: 1 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={resetToDefault}
            disabled={readOnly}
          >
            Reset
          </Button>
          {onSave && (
            <Button
              variant="contained"
              onClick={() => onSave(players)}
            >
              Save State
            </Button>
          )}
        </Box>
      </Box>
      
      <Box sx={{ mb: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <ToggleButtonGroup
          value={currentAction}
          exclusive
          onChange={(_, newAction) => {
            if (newAction) {
              setCurrentAction(newAction);
              // Reset selected voter when switching away from vote mode
              if (newAction !== 'vote') {
                setSelectedVoter(null);
                setHoverTarget(null);
              }
            }
          }}
          aria-label="player action"
          size="small"
          disabled={readOnly}
        >
          <ToggleButton value="role" aria-label="change role">
            Change Role
          </ToggleButton>
          <ToggleButton value="alive" aria-label="toggle alive">
            Alive/Dead
          </ToggleButton>
          <ToggleButton value="check" aria-label="toggle check">
            Sheriff Check
          </ToggleButton>
          <ToggleButton value="vote" aria-label="record votes">
            Votes
          </ToggleButton>
        </ToggleButtonGroup>
        
        {currentAction === 'vote' && (
          <Typography variant="caption" sx={{ fontStyle: 'italic', mt: 1 }}>
            {selectedVoter 
              ? `Select who player ${selectedVoter} voted for...` 
              : 'Select a player who is voting...'}
          </Typography>
        )}
      </Box>
      
      {/* Game board with players in a circle */}
      <Box 
        sx={{ 
          position: 'relative', 
          height: '450px', 
          width: '450px',
          mt: 4,
          mb: 2
        }}
      >
        {/* Draw arrow between selected voter and hover target */}
        {currentAction === 'vote' && selectedVoter !== null && hoverTarget !== null && selectedVoter !== hoverTarget && (
          <ArrowBetweenPlayers 
            players={players}
            selectedId={selectedVoter}
            targetId={hoverTarget}
            calculatePosition={calculatePosition}
            totalPlayers={players.length}
            radius={190}
          />
        )}
        
        {players.map((player, index) => {
          const { left, top } = calculatePosition(index, players.length, 190);
          return (
            <Box
              key={player.id}
              sx={{
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                transform: 'translate(-50%, -50%)',
                // Highlight the selected voter
                ...(currentAction === 'vote' && selectedVoter === player.id && {
                  filter: 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.8))',
                  zIndex: 10
                })
              }}
              onMouseEnter={() => {
                if (currentAction === 'vote' && selectedVoter !== null && selectedVoter !== player.id) {
                  setHoverTarget(player.id);
                }
              }}
              onMouseLeave={() => {
                if (currentAction === 'vote') {
                  setHoverTarget(null);
                }
              }}
            >
              <PlayerCircle
                player={player}
                onClick={handlePlayerClick}
                onRightClick={handlePlayerRightClick}
                size={65}
              />
            </Box>
          );
        })}
      </Box>
      
      {/* Legend */}
      <Paper sx={{ p: 2, mt: 2, background: 'rgba(0,0,0,0.05)', width: '100%', maxWidth: '600px' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>Legend:</Typography>
        <MuiGrid container spacing={2} sx={{ justifyContent: 'center' }}>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#e57373' }} />
              <Typography variant="body2">Civilian</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#424242', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiberManualRecordIcon style={{ color: 'white', fontSize: '10px' }} />
              </Box>
              <Typography variant="body2">Mafia</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#212121', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PanToolIcon style={{ color: 'white', fontSize: '12px' }} />
              </Box>
              <Typography variant="body2">Don</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#c62828', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LocalPoliceIcon style={{ color: 'white', fontSize: '12px' }} />
              </Box>
              <Typography variant="body2">Sheriff</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#c8b3c4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <HelpOutlineIcon style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: '12px' }} />
              </Box>
              <Typography variant="body2">Unknown</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: '#e57373',
                opacity: 0.4
              }} />
              <Typography variant="body2">Dead Player</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 18, 
                height: 18, 
                borderRadius: '50%', 
                backgroundColor: '#e57373',
                border: '2px dashed #ffd700'
              }} />
              <Typography variant="body2">Checked by Sheriff</Typography>
            </Box>
          </MuiGrid>
          
          <MuiGrid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                padding: '1px 4px',
                borderRadius: '4px',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                ➔ 2, 5
              </Box>
              <Typography variant="body2">Player's votes (shown below player)</Typography>
            </Box>
          </MuiGrid>
          
          <MuiGrid item xs={12}>
            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
              In Vote mode: Click first player who's voting, then click target player. Right-click to clear all votes from a player.
            </Typography>
          </MuiGrid>
        </MuiGrid>
      </Paper>
    </Paper>
  );
};

export default MafiaSimulator;